import React from "react";
import { ArrowRight, ArrowDown, Lock, Eye, ShieldCheck } from "lucide-react";
import { OriginBadge } from "../../../lib/origin.jsx";
import { cn } from "../../../lib/utils";
import { col } from "./kit";

// A left-to-right process (top-to-bottom on small screens).
export function Flow({ steps }) {
  return (
    <ol className="flex flex-col md:flex-row md:items-stretch gap-2" data-testid="kc-diagram-flow">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <li className="flex-1 min-w-0 border rounded-sm p-3 bg-bg/60" style={{ borderColor: col(s.tone || "primary", 0.45) }}>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full font-mono-tabular text-[10px] font-bold flex items-center justify-center shrink-0"
                    style={{ background: col(s.tone || "primary", 0.15), color: col(s.tone || "primary") }}>{i + 1}</span>
              <span className="text-[12.5px] font-semibold leading-tight">{s.title}</span>
            </div>
            <div className="text-[11.5px] text-muted mt-1.5 leading-snug">{s.note}</div>
          </li>
          {i < steps.length - 1 && (
            <li aria-hidden className="flex items-center justify-center text-subtle shrink-0">
              <ArrowRight size={14} className="hidden md:block" /><ArrowDown size={14} className="md:hidden" />
            </li>
          )}
        </React.Fragment>
      ))}
    </ol>
  );
}

const LABELS = [
  ["actual", "Built only from 1Rotary's ingested data or a real external source (e.g. NASA weather).", "Use it as a real figure."],
  ["synthetic", "Sample data generated for the pilot until real data is connected.", "Learn from it — never present it as 1Rotary actuals."],
  ["mixed", "Combines both, e.g. sample sales with actual weather.", "Check which part is actual before quoting it."],
];

export function OriginLabels() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="kc-diagram-origin">
      {LABELS.map(([o, what, use]) => (
        <div key={o} className="border border-border rounded-sm p-3 bg-bg/60">
          <OriginBadge origin={o} source={what} testid={`kc-origin-${o}`} />
          <p className="text-[12px] mt-2 leading-snug">{what}</p>
          <p className="text-[11.5px] text-muted mt-1.5 leading-snug">{use}</p>
        </div>
      ))}
    </div>
  );
}

const ROWS = ["A", "B", "C"], COLS = [["X", "steady"], ["Y", "variable"], ["Z", "volatile"]];
const CELL_TONE = { AX: "success", BX: "success", CX: "success", AY: "info", BY: "info", CY: "info", AZ: "warning", BZ: "warning", CZ: "critical" };

export function PolicyMatrix({ cells, highlight = [], zi }) {
  return (
    <div data-testid="kc-diagram-matrix" className="min-w-[440px]">
      <div className="grid grid-cols-[3rem_repeat(3,minmax(0,1fr))] gap-1.5">
        <span />
        {COLS.map(([c, d]) => <div key={c} className="text-center text-[11px] font-semibold uppercase tracking-widest text-muted pb-1">{c} · {d}</div>)}
        {ROWS.map(r => (
          <React.Fragment key={r}>
            <div className="flex items-center justify-center font-display text-lg font-semibold">{r}</div>
            {COLS.map(([c]) => {
              const k = r + c, hot = highlight.includes(k);
              return (
                <div key={k} data-testid={`kc-matrix-${k}`} className={cn("rounded-sm border p-2.5 min-h-[74px] transition-transform", hot && "ring-2 ring-primary scale-[1.02]")}
                     style={{ borderColor: col(CELL_TONE[k], 0.45), background: col(CELL_TONE[k], 0.07) }}>
                  <div className="font-mono-tabular text-[12px] font-bold" style={{ color: col(CELL_TONE[k]) }}>{k}</div>
                  <div className="text-[11.5px] leading-snug mt-1">{cells[k]}</div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      {zi && <div className="mt-2 ml-[3.4rem] rounded-sm border border-dashed border-border px-3 py-2 text-[11.5px]"><span className="font-mono-tabular font-bold mr-2">Z-Int</span>{zi}</div>}
    </div>
  );
}

const Chip = ({ c }) => <span className="font-mono-tabular text-[11px] font-semibold px-1.5 py-0.5 border border-border rounded-sm bg-surface-2">{c}</span>;

export function ClassShift({ moves }) {
  return (
    <div className="divide-y divide-border border border-border rounded-sm" data-testid="kc-diagram-shift">
      {moves.map(m => (
        <div key={m.sku} className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.2fr)_auto_minmax(0,1.4fr)] gap-x-4 gap-y-1 items-center px-3 py-2.5">
          <span className="text-[12.5px] font-medium">{m.sku}</span>
          <span className="inline-flex items-center gap-1.5"><Chip c={m.from} /><ArrowRight size={12} className="text-primary" /><Chip c={m.to} /></span>
          <span className="text-[12px] text-muted">{m.why}</span>
        </div>
      ))}
    </div>
  );
}

function Record({ title, icon: Icon, fields, full }) {
  return (
    <div className="border border-border rounded-sm bg-bg/60 flex-1 min-w-0">
      <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-widest font-semibold text-muted inline-flex items-center gap-1.5 w-full"><Icon size={11} />{title}</div>
      {fields.map(f => {
        const masked = !full && f.masked;
        return (
          <div key={f.label} className="grid grid-cols-[6.5rem_1fr] gap-2 px-3 py-1.5 text-[12px] border-b border-border/60 last:border-0">
            <span className="text-muted inline-flex items-center gap-1">{masked && <Lock size={10} className="text-warning" />}{f.label}</span>
            <span className={cn("font-mono-tabular break-all", masked && "text-muted")}>{masked ? f.masked : f.full}</span>
          </div>
        );
      })}
    </div>
  );
}

export function Masking({ fields }) {
  return (
    <div className="flex flex-col md:flex-row gap-3" data-testid="kc-diagram-masking">
      <Record title="Any other role" icon={Eye} fields={fields} full={false} />
      <Record title="IT Head" icon={ShieldCheck} fields={fields} full />
    </div>
  );
}
